import { useMemo } from 'react';
import ResourceHandler from 'applications/common/resourcehandler/index.js';
import { getRouteResolver } from 'applications/common/stores/router/selectors.js';
import MojitoServices from 'mojito/services';
import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import { noop } from 'mojito/utils';
import analyticsEmitter from 'applications/sports/analytics/analytics-emitter.js';
import subscriptionResolverFactory from 'applications/sports/subscription-resolver/subscription-resolver-factory.js';
import { connect } from 'react-redux';
import PerformanceEmitter from 'applications/sports/performance/performance-emitter.js';
import Routing from './routing/index.js';
import AppViewSportsbook from './view/index.js';
const reduxInstance = MojitoCore.Services.redux;
const { useStringResolver, useTranslations, useAppContext } = MojitoCore.Presentation.Hooks;
const AppContextProvider = MojitoCore.Presentation.AppContext.Context.Provider;
const AppContextBuilder = MojitoCore.Presentation.AppContext.ContextBuilder;
const AppContextExtender = MojitoCore.Presentation.AppContext.ContextExtender;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { LoadingView, ComponentErrorView } = MojitoPresentation.Components;
const { OVERLAY_INTERACTION } = MojitoPresentation.Base.Intent.Types;
const intentActions = MojitoCore.Intents.actions;
const { useGlobalInteractionMetric } = MojitoModules.Performance.Hooks;
const {
    selectState: selectSystemSettings,
    selectAdditionalContext,
    selectApplicationMode,
    subscribe: subscribeSystemSettings,
} = MojitoCore.Services.SystemSettings.selectors;
const { selectors: userSettingsSelectors, slice: userSettingsSlice } = MojitoServices.UserSettings;
const { selectUserSettings, subscribe: subscribeUserSettings } = userSettingsSelectors;
const { STORE_KEY: USER_SETTINGS_STORE_KEY } = userSettingsSlice;
const { descriptor: SportsDataDescriptor } = MojitoServices.SportsContent.Sports;

const { selectSport } = MojitoServices.SportsContent.Sports.selectors;
const { selectLoginState } = MojitoServices.Authentication.selectors;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;

const sportName = sportId => selectSport(sportId)?.name || '';

class AppControllerSportsbook extends UIViewImplementation {
    constructor(props) {
        super(props);
        this.unsubscribeLanguageChange = noop;
        this.unsubscribeApplicationModeChange = noop;
        this.isContentShown = (routingData, strict = false) => {
            const { location } = this.props;
            return getRouteResolver().isMatchLocation(routingData, location, strict);
        };

        const uiContextPath = () => {
            const additionalContext = selectAdditionalContext();
            const context = selectApplicationMode();
            return additionalContext ? `${additionalContext}_${context}` : context;
        };

        const appContext = new AppContextBuilder()
            .withSystemSettings(() => selectSystemSettings())
            .withUserSettings(() => selectUserSettings())
            .withSportName(sportName)
            .withGetLoginState(() => selectLoginState())
            .withIsContentShown(this.isContentShown)
            .withGetImage(path => ResourceHandler.getImage(path))
            .withGetFixture(path => ResourceHandler.getFixture(path))
            .withUiContextPath(uiContextPath())
            .withAnalyticsEmitter(analyticsEmitter)
            .withOverlayInteraction(() =>
                this.props.dispatch(intentActions.publishIntent(OVERLAY_INTERACTION))
            )
            .withPerformanceEmitter(new PerformanceEmitter())
            .withPathname(props.location.pathname)
            .withSuspenseFallback(LoadingView)
            .withErrorFallback(ComponentErrorView)
            .withSubscriptionResolver(subscriptionResolverFactory.getSubscriptionResolver())
            .build();

        // Store location.pathname to detect changes in the future.
        this.pathname = props.location.pathname;

        this.state = { appContext };
    }

    componentDidMount() {
        const subscriptionResolver = subscriptionResolverFactory.getSubscriptionResolver();
        if (this.config.reloadOnLanguageChange) {
            this.unsubscribeLanguageChange = subscribeSystemSettings('language', () => {
                // Reload on a microtask to allow other subscribers to execute, e.g. analytics wants to report language change.
                queueMicrotask(() => window.location.reload());
            });
        }
        if (this.config.reloadOnApplicationModeChange) {
            this.unsubscribeApplicationModeChange = subscribeSystemSettings(
                'applicationMode',
                () => {
                    // Reload on a microtask to allow other potential subscribers to execute.
                    queueMicrotask(() => window.location.reload());
                }
            );
        }
        this.unsubscribeAdditionalContextChange = subscribeSystemSettings(
            'additionalContext',
            () => {
                const contextDelta = new AppContextBuilder().withSystemSettings(() =>
                    selectSystemSettings()
                );
                this.updateAppContext(contextDelta);
            }
        );
        this.unsubscribeUserSettings = subscribeUserSettings('oddsFormat', () => {
            // Reload on a microtask to allow other subscribers to execute, e.g. analytics wants to report language change.
            queueMicrotask(() => {
                window.location.reload();
            });
        });
        this.unwatchUserSettings = reduxInstance.watch(USER_SETTINGS_STORE_KEY, () => {
            const contextDelta = new AppContextBuilder().withUserSettings(() =>
                selectUserSettings()
            );
            this.updateAppContext(contextDelta);
        });
        this.controllerHelper().requestData(
            SportsDataDescriptor.create(subscriptionResolver.sports())
        );
    }

    componentWillUnmount() {
        this.unsubscribeLanguageChange();
        this.unsubscribeApplicationModeChange();
        this.unsubscribeAdditionalContextChange();
        this.unsubscribeUserSettings();
        this.unwatchUserSettings();
    }

    componentDidUpdate() {
        const newPathName = this.props.location.pathname;
        // We can't compare this.props.location with prevProps.location coz it is mutable object.
        if (newPathName !== this.pathname) {
            this.pathname = newPathName;
            const contextDelta = new AppContextBuilder()
                .withIsContentShown(this.isContentShown)
                .withPathname(this.pathname)
                .build();
            // Updating context to force re-render for components interested in appContext.isContentShown().
            this.updateAppContext(contextDelta);
        }
    }

    updateAppContext(newContext) {
        const appContext = {
            ...this.state.appContext,
            ...newContext,
        };
        this.setState({ appContext });
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(nextProps, this.props) || !isShallowEqual(nextState, this.state);
    }

    render() {
        return (
            <AppContextProvider value={this.state.appContext}>
                <AppViewModule {...this.props} config={this.config} />
            </AppContextProvider>
        );
    }
}

const AppViewModule = props => {
    // Catch user interactions with application
    useGlobalInteractionMetric(props.config.interactionEvents);

    const appContext = useAppContext();
    const language = appContext.systemSettings()?.language;
    const l10n = useTranslations(language);
    const stringResolver = useStringResolver(l10n);

    const routeResolverContext = useMemo(() => {
        const routeResolver = getRouteResolver();
        routeResolver.setStringResolver(stringResolver);
        return { routeResolver };
    }, [stringResolver]);

    return (
        <AppContextExtender value={routeResolverContext}>
            <AppViewSportsbook>
                <Routing />
            </AppViewSportsbook>
        </AppContextExtender>
    );
};

export default connect()(AppControllerSportsbook);
